/**
 * PD-20: "Save & finish later" resume support.
 *
 * Maps a PartialApplicationSerializer response (GET
 * lender/partial-application/{slug}/?uuid=...) onto the new stepper's
 * GlobalContext shape (state.questions / .business / .personal), and infers
 * which of the 6 stepper steps (steps.config.js) the borrower should land
 * back on, mirroring what the old per-field flow did for its own "Send
 * Reminder" resume links, adapted for the 6-step shape instead of ~28
 * individual screens.
 */

const isFilled = (value) => value !== null && value !== undefined && value !== "";

export function formatAddress(address) {
  if (!address) return "";
  const line1 = [address.house_number, address.street_name].filter(Boolean).join(" ").trim();
  const line2 = [address.city, [address.state, address.zip_code].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
  return [line1, line2].filter(Boolean).join(", ");
}

/**
 * Rebuilds the Google Places-shaped `addressComponents` the steps validate
 * against (helpers.extractAddressComponents) from a saved Address. A fresh
 * session only ever has those in localStorage from the original browser
 * session's autocomplete pick — so without this a resumed address shows
 * its text but fails "Please select your address from the list" on
 * Continue. `state` was saved in its short form ("NY"), so short and long
 * text both carry what was saved.
 */
export function addressToComponents(address) {
  if (!address) return [];
  const component = (type, value) =>
    isFilled(value) ? [{ types: [type], longText: String(value), shortText: String(value) }] : [];
  return [
    ...component("street_number", address.house_number),
    ...component("route", address.street_name),
    ...component("locality", address.city),
    ...component("administrative_area_level_1", address.state),
    ...component("country", address.country),
    ...component("postal_code", address.zip_code),
  ];
}

// localStorage key each step's address autocomplete reads its components
// from, keyed by the PartialApplication field it was saved as.
const ADDRESS_COMPONENT_STORAGE_KEYS = {
  company_address: "companyComponentsMazo",
  home_address: "homeComponentsMazo",
  personal_home_address: "residentialComponentsMazo",
  personal_mailing_address: "mailingComponentsMazo",
  personal_employer_address: "employerComponentsMazo",
  personal_co_applicant_home_address: "coApplicantResidentialComponentsMazo",
  personal_co_applicant_employer_address: "coApplicantEmployerComponentsMazo",
};

/**
 * Seeds those localStorage keys from the resumed application before the
 * stepper mounts. Keys for addresses not saved yet are cleared, so another
 * application's leftover components in the same browser can't pair with
 * this one's (blank) address field.
 */
export function restoreAddressComponents(data) {
  Object.entries(ADDRESS_COMPONENT_STORAGE_KEYS).forEach(([field, key]) => {
    try {
      if (data?.[field]) {
        localStorage.setItem(key, JSON.stringify(addressToComponents(data[field])));
      } else {
        localStorage.removeItem(key);
      }
    } catch (err) {}
  });
}

const unitNumberOf = (address) => address?.unit_number || "";

export function mapResumeDataToState(data, currentQuestions) {
  const questions = {
    ...currentQuestions,
    ...(isFilled(data.email) && { email: data.email }),
    ...(isFilled(data.phone_no) && { phone: data.phone_no }),
    ...(isFilled(data.first_name) && { firstname: data.first_name }),
    ...(isFilled(data.last_name) && { lastname: data.last_name }),
    ...(isFilled(data.date_of_birth) && { dob: data.date_of_birth }),
    ...(isFilled(data.ssn_number) && { ssn: data.ssn_number }),
    ...(isFilled(data.citizenship) && { citizenship: data.citizenship }),
    ...(isFilled(data.equipment_use) && { equipmentUse: data.equipment_use }),
    ...(isFilled(data.requested_amount) && { requestedAmount: data.requested_amount }),
    ...(isFilled(data.url_equipment_price) && { urlEquipmentPrice: data.url_equipment_price }),
    ...(isFilled(data.purple_wave_customer_number) && {
      purpleWaveCustomerNumber: data.purple_wave_customer_number,
    }),
    // An ICN the borrower typed on the Equipment step (not in the link, so
    // the resume link can't carry it) — pre-fills that field again (PD-8).
    ...(isFilled(data.purple_wave_icn) &&
      !currentQuestions?.purpleWaveIcnFromLink && { purpleWaveIcn: data.purple_wave_icn }),
    ...(isFilled(data.marketing_equipment_price) && {
      equipmentCost: data.marketing_equipment_price,
    }),
    ...(data.has_sales_rep != null && { hasSalesRep: data.has_sales_rep }),
    ...(isFilled(data.sales_rep_first_name) && { salesRepFirstName: data.sales_rep_first_name }),
    ...(isFilled(data.sales_rep_last_name) && { salesRepLastName: data.sales_rep_last_name }),
  };

  const business = {
    ...currentQuestions?.business,
    ...(isFilled(data.company_name) && { companyName: data.company_name }),
    ...(isFilled(data.entity_type) && { entityType: data.entity_type }),
    ...(isFilled(data.business_duration) && { longBussiness: data.business_duration }),
    ...(isFilled(data.business_description) && { businessDescription: data.business_description }),
    ...(isFilled(data.annual_business_revenue) && { bussinessAnual: data.annual_business_revenue }),
    ...(data.company_address && {
      companyAddress: formatAddress(data.company_address),
      companyUnitNumber: unitNumberOf(data.company_address),
    }),
    ...(data.home_address && {
      homeAddress: formatAddress(data.home_address),
      homeUnitNumber: unitNumberOf(data.home_address),
    }),
    ...(isFilled(data.tax_id) && { taxId: data.tax_id }),
    ...(isFilled(data.ownership_percentage) && { ownershipPercentage: data.ownership_percentage }),
    ...(isFilled(data.down_payment_percentage) && { percentage: data.down_payment_percentage }),
    ...(isFilled(data.second_owner_email) && { secondOwnerEmail: data.second_owner_email }),
    ...(isFilled(data.second_owner_phone_no) && { secondOwnerPhone: data.second_owner_phone_no }),
    ...(isFilled(data.second_owner_first_name) && {
      secondOwnerFirstName: data.second_owner_first_name,
    }),
    ...(isFilled(data.second_owner_last_name) && {
      secondOwnerLastName: data.second_owner_last_name,
    }),
    ...(isFilled(data.second_owner_date_of_birth) && {
      secondOwnerDob: data.second_owner_date_of_birth,
    }),
    ...(isFilled(data.second_owner_ssn_number) && { secondOwnerSSN: data.second_owner_ssn_number }),
    ...(data.second_owner_home_address && {
      secondOwnerHomeAddress: formatAddress(data.second_owner_home_address),
    }),
    // Full multi-owner list (PD-6 "3+ owners" support). Owner rows keep
    // their address components in state rather than localStorage, so
    // they're rebuilt right here (see addressToComponents).
    ...(Array.isArray(data.additional_owners) &&
      data.additional_owners.length > 0 && {
        additionalOwners: data.additional_owners.map((owner) => ({
          order: owner.order,
          email: owner.email || "",
          phone: owner.phone_no || "",
          firstName: owner.first_name || "",
          lastName: owner.last_name || "",
          dob: owner.date_of_birth || "",
          ssn: owner.ssn_number || "",
          ownershipPercentage: isFilled(owner.ownership_percentage)
            ? owner.ownership_percentage
            : "",
          homeAddress: owner.home_address ? formatAddress(owner.home_address) : "",
          homeAddressComponents: addressToComponents(owner.home_address),
          unitNumber: unitNumberOf(owner.home_address),
        })),
      }),
    ...(isFilled(data.purchase_type) && { purchaseType: data.purchase_type }),
    ...(isFilled(data.replacing_description) && {
      replacingDescription: data.replacing_description,
    }),
    ...(isFilled(data.replacing_disposition) && {
      replacingDisposition: data.replacing_disposition,
    }),
  };

  const personal = {
    ...currentQuestions?.personal,
    ...(isFilled(data.personal_suffix) && { suffix: data.personal_suffix }),
    ...(isFilled(data.personal_marital_status) && { maritalStatus: data.personal_marital_status }),
    ...(data.personal_home_address && {
      residentialAddress: formatAddress(data.personal_home_address),
      residentialUnitNumber: unitNumberOf(data.personal_home_address),
      // A BooleanField defaulting to False in the DB, so it only means
      // anything once the Residence step (which saves both) was completed.
      isPlanToRegisterVehicle: Boolean(data.personal_is_plan_to_register_vehicle),
    }),
    ...(data.personal_mailing_address && {
      mailingAddress: formatAddress(data.personal_mailing_address),
      mailingUnitNumber: unitNumberOf(data.personal_mailing_address),
    }),
    ...(isFilled(data.personal_living_years) && { livingYears: data.personal_living_years }),
    ...(isFilled(data.personal_living_months) && { livingMonths: data.personal_living_months }),
    ...(isFilled(data.personal_monthly_payment) && {
      livingMonthlyPayment: data.personal_monthly_payment,
    }),
    ...(isFilled(data.personal_living_status) && { livingStatus: data.personal_living_status }),
    ...(isFilled(data.personal_employment_status) && {
      employmentStatus: data.personal_employment_status,
    }),
    ...(isFilled(data.personal_employer_business_name) && {
      businessName: data.personal_employer_business_name,
    }),
    ...(isFilled(data.personal_employer_business_phone_no) && {
      businessPhone: data.personal_employer_business_phone_no,
    }),
    ...(isFilled(data.personal_occupation_title) && {
      occupationTitle: data.personal_occupation_title,
    }),
    ...(data.personal_employer_address && {
      employerAddress: formatAddress(data.personal_employer_address),
      employerUnitNumber: unitNumberOf(data.personal_employer_address),
    }),
    ...(isFilled(data.personal_retirement_income) && {
      retirementIncome: data.personal_retirement_income,
    }),
    ...(isFilled(data.personal_retirement_years) && {
      retirementYears: data.personal_retirement_years,
    }),
    ...(isFilled(data.personal_retirement_months) && {
      retirementMonths: data.personal_retirement_months,
    }),
    ...(isFilled(data.personal_gross_monthly_income) && {
      grossMonthlyIncome: data.personal_gross_monthly_income,
    }),
    ...(isFilled(data.personal_employment_years) && {
      employmentYears: data.personal_employment_years,
    }),
    ...(isFilled(data.personal_employment_months) && {
      employmentMonths: data.personal_employment_months,
    }),
    ...(isFilled(data.personal_additional_income_source) && {
      additionalIncomeSource: data.personal_additional_income_source,
    }),
    ...(isFilled(data.personal_additional_income_monthly) && {
      additionalMonthlyIncome: data.personal_additional_income_monthly,
    }),
    ...(data.is_co_applicant != null && { isCoApplicant: data.is_co_applicant }),
    ...(isFilled(data.personal_co_applicant_first_name) && {
      coApplicantFirstName: data.personal_co_applicant_first_name,
    }),
    ...(isFilled(data.personal_co_applicant_last_name) && {
      coApplicantLastName: data.personal_co_applicant_last_name,
    }),
    ...(isFilled(data.personal_co_applicant_email) && {
      coApplicantEmail: data.personal_co_applicant_email,
    }),
    ...(isFilled(data.personal_co_applicant_phone_no) && {
      coApplicantPhone: data.personal_co_applicant_phone_no,
    }),
    ...(isFilled(data.personal_co_applicant_ssn) && {
      coApplicantSsn: data.personal_co_applicant_ssn,
    }),
    ...(isFilled(data.personal_co_applicant_citizenship) && {
      coApplicantCitizenship: data.personal_co_applicant_citizenship,
    }),
    ...(isFilled(data.personal_co_applicant_suffix) && {
      coApplicantSuffix: data.personal_co_applicant_suffix,
    }),
    ...(isFilled(data.personal_co_applicant_marital_status) && {
      coApplicantMaritalStatus: data.personal_co_applicant_marital_status,
    }),
    ...(data.personal_co_applicant_home_address && {
      coApplicantResidentialAddress: formatAddress(data.personal_co_applicant_home_address),
      coApplicantResidentialUnitNumber: unitNumberOf(data.personal_co_applicant_home_address),
    }),
    ...(isFilled(data.personal_co_applicant_living_years) && {
      coApplicantLivingYears: data.personal_co_applicant_living_years,
    }),
    ...(isFilled(data.personal_co_applicant_living_months) && {
      coApplicantLivingMonths: data.personal_co_applicant_living_months,
    }),
    ...(isFilled(data.personal_co_applicant_monthly_payment) && {
      coApplicantLivingMonthlyPayment: data.personal_co_applicant_monthly_payment,
    }),
    ...(isFilled(data.personal_co_applicant_living_status) && {
      coApplicantLivingStatus: data.personal_co_applicant_living_status,
    }),
    ...(isFilled(data.personal_co_applicant_employment_status) && {
      coApplicantEmploymentStatus: data.personal_co_applicant_employment_status,
    }),
    ...(isFilled(data.personal_co_applicant_employer_business_name) && {
      coApplicantBusinessName: data.personal_co_applicant_employer_business_name,
    }),
    ...(isFilled(data.personal_co_applicant_gross_monthly_income) && {
      coApplicantGrossMonthlyIncome: data.personal_co_applicant_gross_monthly_income,
    }),
    ...(data.personal_co_applicant_employer_address && {
      coApplicantEmployerAddress: formatAddress(data.personal_co_applicant_employer_address),
      coApplicantEmployerUnitNumber: unitNumberOf(data.personal_co_applicant_employer_address),
    }),
    ...(isFilled(data.personal_co_applicant_retirement_income) && {
      coApplicantretirementIncome: data.personal_co_applicant_retirement_income,
    }),
    ...(isFilled(data.personal_co_applicant_retirement_years) && {
      coApplicantretirementYears: data.personal_co_applicant_retirement_years,
    }),
    ...(isFilled(data.personal_co_applicant_retirement_months) && {
      coApplicantretirementMonths: data.personal_co_applicant_retirement_months,
    }),
    ...(isFilled(data.personal_co_applicant_employment_years) && {
      coApplicantEmploymentYears: data.personal_co_applicant_employment_years,
    }),
    ...(isFilled(data.personal_co_applicant_employment_months) && {
      coApplicantEmploymentMonths: data.personal_co_applicant_employment_months,
    }),
    ...(isFilled(data.personal_co_applicant_additional_income_source) && {
      coApplicantAdditionalIncomeSource: data.personal_co_applicant_additional_income_source,
    }),
    ...(isFilled(data.personal_co_applicant_additional_income_monthly) && {
      coApplicantAdditionalMonthlyIncome: data.personal_co_applicant_additional_income_monthly,
    }),
    ...(isFilled(data.personal_co_applicant_date_of_birth) && {
      coApplicantDob: data.personal_co_applicant_date_of_birth,
    }),
    ...(isFilled(data.personal_down_payment) && { downPayment: data.personal_down_payment }),
    ...(isFilled(data.year) && { financingYear: data.year }),
    ...(isFilled(data.make) && { financingMake: data.make }),
    ...(isFilled(data.model) && { financingModel: data.model }),
  };

  return { questions, business, personal };
}

/**
 * The current page's query string minus our own `uuid`, to forward to the
 * "Save & finish later" endpoint so the emailed resume link keeps the
 * borrower's original third-party params (Purple Wave's icn/listing_url,
 * Soarr's utm_source/stock_number, ...) whatever their keys. Re-saving from
 * an already-resumed link forwards the same params again, not the old uuid.
 */
export function getForwardableQueryString(search) {
  // Filtered as raw `key=value` pieces rather than re-serialized through
  // URLSearchParams, so every other param keeps its exact original text
  // (e.g. `%20` stays `%20`, `/` stays `/`) and the link looks unchanged.
  const keyOf = (part) => {
    const rawKey = part.split("=")[0];
    try {
      return decodeURIComponent(rawKey.replace(/\+/g, " "));
    } catch (err) {
      return rawKey;
    }
  };
  return String(search || "")
    .replace(/^\?/, "")
    .split("&")
    .filter((part) => part && keyOf(part) !== "uuid")
    .join("&");
}

/**
 * Resume skips GetStarted.js, which is where a fresh visit reads
 * year/make/model from the URL into `personal` — so the resume flow applies
 * the same URL prefill here, with the same lowercase-key matching and
 * `isFinancing*Prefilled` flags. Values the borrower already saved on the
 * Equipment step (restored from the DB into `personal`) take precedence.
 */
export function applyUrlEquipmentPrefill(personal, search) {
  const queryParams = {};
  new URLSearchParams(search).forEach((value, key) => {
    queryParams[key.toLowerCase()] = value;
  });
  return {
    ...personal,
    financingYear: isFilled(personal?.financingYear) ? personal.financingYear : queryParams.year || "",
    financingMake: isFilled(personal?.financingMake) ? personal.financingMake : queryParams.make || "",
    financingModel: isFilled(personal?.financingModel)
      ? personal.financingModel
      : queryParams.model || "",
    isFinancingYearPrefilled: !!queryParams.year,
    isFinancingMakePrefilled: !!queryParams.make,
    isFinancingModelPrefilled: !!queryParams.model,
  };
}

/**
 * Which of the 6 steps.config.js steps (0-5) to land the borrower back on:
 * the step after the furthest one they completed. A step's fields only
 * reach the DB on its own Continue, so a step with saved data is a finished
 * one — the borrower pressed "Save & finish later" from the step after it.
 * Offers (5) is never inferred here since a PartialApplication that's still
 * fetchable by this endpoint hasn't been submitted yet.
 */
export function inferResumeStepIndex(data, isPersonalFlow) {
  if (isFilled(data.signature)) return 4; // Review & Sign

  // Fields only the Equipment step itself saves — not
  // marketing_equipment_price, which application creation can already set
  // from a ?price= link param before the borrower reaches that step.
  if (
    isFilled(data.year) ||
    isFilled(data.make) ||
    isFilled(data.model) ||
    isFilled(data.down_payment_percentage) ||
    isFilled(data.purchase_type)
  ) {
    return 4; // Equipment done -> Review & Sign
  }

  if (isPersonalFlow) {
    if (isFilled(data.personal_employment_status) || isFilled(data.personal_gross_monthly_income)) {
      return 3; // Employment done -> Equipment
    }
    if (
      data.personal_home_address ||
      data.personal_mailing_address ||
      isFilled(data.personal_marital_status)
    ) {
      return 2; // Residence done -> Employment
    }
  } else {
    if (
      isFilled(data.ownership_percentage) ||
      isFilled(data.tax_id) ||
      isFilled(data.second_owner_email)
    ) {
      return 3; // Ownership done -> Equipment
    }
    if (
      isFilled(data.company_name) ||
      isFilled(data.entity_type) ||
      isFilled(data.annual_business_revenue) ||
      data.company_address ||
      data.home_address
    ) {
      return 2; // Business done -> Ownership
    }
  }

  if (isFilled(data.first_name) || isFilled(data.last_name)) {
    return 1; // You done -> Business / Residence
  }

  return 0; // You
}
